/**
 * Families [New] — vanilla JS tab scene
 * Matches VG-react Families.jsx (vertical slide + copy/ghost fade + tab underline)
 * Transitions are interruptible so rapid hover/click always follows the latest tab.
 * Mobile: copy truncates + Read More; signature sits below the scene.
 */
(function () {
  'use strict';

  var SECTION_TYPE = 'families-new';
  var SLIDE_MS = 1800;
  var COPY_MS = 550;
  var GHOST_MS = 700;
  var MQ_MOBILE = '(max-width: 767px)';
  var instances = new WeakMap();

  function pad2(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function isMobile() {
    return typeof window.matchMedia === 'function' && window.matchMedia(MQ_MOBILE).matches;
  }

  function FamiliesNew(root) {
    this.root = root;
    this.index = 0;
    this.total = parseInt(root.getAttribute('data-family-count'), 10) || 0;
    this.copyChars = parseInt(root.getAttribute('data-copy-chars'), 10) || 90;
    this.timers = [];
    this.reducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.reducedMotion) {
      root.classList.add('is-reduced-motion');
    }

    this.bgPanels = Array.prototype.slice.call(root.querySelectorAll('.vg-families__bg-panel'));
    this.scenePanels = Array.prototype.slice.call(root.querySelectorAll('.vg-families__scene-panel'));
    this.copies = Array.prototype.slice.call(root.querySelectorAll('[data-family-copy]'));
    this.signatures = Array.prototype.slice.call(root.querySelectorAll('[data-family-signature]'));
    this.ghosts = Array.prototype.slice.call(root.querySelectorAll('[data-family-ghost]'));
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-family-tab]'));
    this.underline = root.querySelector('[data-family-underline]');
    this.currentEl = root.querySelector('[data-family-current]');
    this.overlineNum = root.querySelector('[data-family-overline-num]');
    this.cta = root.querySelector('[data-family-cta]');
    this.defaultCtaHref = this.cta ? this.cta.getAttribute('href') || '#' : '#';

    this.copyBodies = [];
    this.copies.forEach(function (el) {
      var body = el.querySelector('[data-family-copy-body]');
      var btn = el.querySelector('[data-family-read-more]');
      if (!body) return;
      this.copyBodies.push({
        wrap: el,
        body: body,
        btn: btn,
        full: (body.textContent || '').replace(/\s+/g, ' ').trim(),
        expanded: false,
      });
    }, this);

    if (this.total < 1) return;

    this.bind();
    this.syncTabs(0, true);
    this.positionUnderline(0, true);
    this.applyCopyTruncation();
  }

  FamiliesNew.prototype.bind = function () {
    var self = this;

    this.tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var i = parseInt(tab.getAttribute('data-family-tab'), 10);
        if (!isNaN(i)) self.goTo(i);
      });

      tab.addEventListener('mouseenter', function () {
        if (isMobile()) return;
        var i = parseInt(tab.getAttribute('data-family-tab'), 10);
        if (!isNaN(i)) self.goTo(i);
      });
    });

    this.copyBodies.forEach(function (item) {
      if (!item.btn) return;
      item.btn.addEventListener('click', function () {
        item.expanded = !item.expanded;
        self.renderCopyItem(item);
      });
    });

    if (typeof ResizeObserver !== 'undefined' && this.underline) {
      this.resizeObserver = new ResizeObserver(function () {
        self.positionUnderline(self.index, self.reducedMotion);
      });
      var tabsWrap = self.root.querySelector('.vg-families__tabs-wrap');
      if (tabsWrap) this.resizeObserver.observe(tabsWrap);
    }

    window.addEventListener('resize', function () {
      self.positionUnderline(self.index, self.reducedMotion);
      self.applyCopyTruncation();
    });
  };

  FamiliesNew.prototype.truncateText = function (text, limit) {
    if (!text || text.length <= limit) return text;
    var slice = text.slice(0, limit);
    var lastSpace = slice.lastIndexOf(' ');
    if (lastSpace > Math.floor(limit * 0.6)) {
      slice = slice.slice(0, lastSpace);
    }
    return slice.replace(/[.,;:!\s]+$/, '') + '...';
  };

  FamiliesNew.prototype.renderCopyItem = function (item) {
    if (!item || !item.body) return;

    if (!isMobile()) {
      item.body.textContent = item.full;
      if (item.btn) {
        item.btn.hidden = true;
        item.btn.setAttribute('aria-expanded', 'false');
        this.syncReadMoreButton(item.btn, false);
      }
      item.expanded = false;
      return;
    }

    var needsTruncate = item.full.length > this.copyChars;
    if (!needsTruncate) {
      item.body.textContent = item.full;
      if (item.btn) item.btn.hidden = true;
      return;
    }

    if (item.expanded) {
      item.body.textContent = item.full;
      if (item.btn) {
        item.btn.hidden = false;
        item.btn.setAttribute('aria-expanded', 'true');
        this.syncReadMoreButton(item.btn, true);
      }
    } else {
      item.body.textContent = this.truncateText(item.full, this.copyChars);
      if (item.btn) {
        item.btn.hidden = false;
        item.btn.setAttribute('aria-expanded', 'false');
        this.syncReadMoreButton(item.btn, false);
      }
    }
  };

  FamiliesNew.prototype.syncReadMoreButton = function (btn, expanded) {
    if (!btn) return;
    var icon = btn.querySelector('.vg-families__read-more-icon');
    var label = btn.querySelector('[data-family-read-more-label]');
    var more = btn.getAttribute('data-label-more') || 'Read More';
    var less = btn.getAttribute('data-label-less') || 'Read Less';
    if (icon) icon.textContent = expanded ? '–' : '+';
    if (label) label.textContent = expanded ? less : more;
  };

  FamiliesNew.prototype.applyCopyTruncation = function () {
    var self = this;
    this.copyBodies.forEach(function (item) {
      if (!isMobile()) item.expanded = false;
      self.renderCopyItem(item);
    });
  };

  FamiliesNew.prototype.clearTimers = function () {
    this.timers.forEach(function (id) {
      window.clearTimeout(id);
    });
    this.timers = [];
  };

  FamiliesNew.prototype.later = function (fn, ms) {
    var self = this;
    var id = window.setTimeout(function () {
      self.timers = self.timers.filter(function (t) {
        return t !== id;
      });
      fn();
    }, ms);
    this.timers.push(id);
  };

  FamiliesNew.prototype.syncTabs = function (index, instant) {
    var self = this;

    this.tabs.forEach(function (tab, i) {
      var active = i === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    if (this.currentEl) {
      this.currentEl.textContent = pad2(index + 1);
    }

    if (this.overlineNum) {
      this.overlineNum.textContent = pad2(index + 1);
    }

    if (this.cta) {
      var activeTab = this.tabs[index];
      var href =
        activeTab && activeTab.getAttribute('data-family-link')
          ? activeTab.getAttribute('data-family-link')
          : this.defaultCtaHref;
      this.cta.setAttribute('href', href || this.defaultCtaHref);
    }

    if (instant || this.reducedMotion) {
      this.positionUnderline(index, true);
    } else {
      window.requestAnimationFrame(function () {
        self.positionUnderline(index, false);
      });
    }
  };

  FamiliesNew.prototype.positionUnderline = function (index, instant) {
    if (!this.underline || !this.tabs[index]) return;

    /* Mobile uses CSS ::after underlines on stacked tabs */
    if (isMobile()) {
      this.underline.hidden = true;
      return;
    }

    var tab = this.tabs[index];
    var wrap = tab.closest('.vg-families__tabs-wrap');
    if (!wrap) return;

    var left = tab.offsetLeft;
    var width = tab.offsetWidth;

    this.underline.hidden = false;
    this.underline.style.width = width + 'px';
    this.underline.style.transform = 'translateX(' + left + 'px)';

    if (instant) {
      this.underline.style.transition = 'none';
      window.requestAnimationFrame(
        function (el) {
          el.style.transition = '';
        }.bind(null, this.underline)
      );
    }

    /* Keep the active tab in view on narrow screens. */
    if (wrap.scrollWidth > wrap.clientWidth) {
      var tabLeft = tab.offsetLeft;
      var tabRight = tabLeft + tab.offsetWidth;
      var viewLeft = wrap.scrollLeft;
      var viewRight = viewLeft + wrap.clientWidth;
      if (tabLeft < viewLeft) {
        wrap.scrollLeft = tabLeft - 12;
      } else if (tabRight > viewRight) {
        wrap.scrollLeft = tabRight - wrap.clientWidth + 12;
      }
    }
  };

  /* Snap panels so only `activeIndex` is on-stage (no mid-animation classes). */
  FamiliesNew.prototype.settlePanels = function (nodes, activeIndex) {
    nodes.forEach(function (el) {
      var i = parseInt(el.getAttribute('data-family-panel'), 10);
      el.style.transition = 'none';
      el.classList.remove('is-exiting', 'is-entering');
      el.classList.toggle('is-active', i === activeIndex);
    });

    /* Force reflow so the next transition starts cleanly from settled state. */
    if (nodes[0]) nodes[0].offsetHeight;

    nodes.forEach(function (el) {
      el.style.transition = '';
    });
  };

  FamiliesNew.prototype.settleFades = function (nodes, activeIndex, attr) {
    nodes.forEach(function (el) {
      var i = parseInt(el.getAttribute(attr), 10);
      el.style.transition = 'none';
      el.classList.remove('is-exiting');
      el.classList.toggle('is-active', i === activeIndex);
    });

    if (nodes[0]) nodes[0].offsetHeight;

    nodes.forEach(function (el) {
      el.style.transition = '';
    });
  };

  FamiliesNew.prototype.swapPanels = function (nodes, from, to) {
    var outgoing = nodes.filter(function (el) {
      return parseInt(el.getAttribute('data-family-panel'), 10) === from;
    })[0];
    var incoming = nodes.filter(function (el) {
      return parseInt(el.getAttribute('data-family-panel'), 10) === to;
    })[0];

    if (!incoming) return;

    if (this.reducedMotion || from === to) {
      this.settlePanels(nodes, to);
      return;
    }

    /* Reset non-participants below the fold so interrupt mid-slide stays clean. */
    nodes.forEach(function (el) {
      var i = parseInt(el.getAttribute('data-family-panel'), 10);
      if (i === from || i === to) return;
      el.style.transition = 'none';
      el.classList.remove('is-active', 'is-exiting', 'is-entering');
      el.offsetHeight;
      el.style.transition = '';
    });

    if (outgoing) {
      outgoing.classList.remove('is-entering');
      outgoing.classList.remove('is-active');
      outgoing.classList.add('is-exiting');
    }

    incoming.classList.remove('is-exiting');
    incoming.classList.add('is-entering');
    incoming.offsetHeight;
    incoming.classList.add('is-active');

    this.later(function () {
      if (outgoing) {
        outgoing.classList.remove('is-exiting', 'is-active');
      }
      incoming.classList.remove('is-entering');
    }, SLIDE_MS);
  };

  FamiliesNew.prototype.swapFade = function (nodes, from, to, attr, duration) {
    var outgoing = nodes.filter(function (el) {
      return parseInt(el.getAttribute(attr), 10) === from;
    })[0];
    var incoming = nodes.filter(function (el) {
      return parseInt(el.getAttribute(attr), 10) === to;
    })[0];

    if (!incoming) return;

    if (this.reducedMotion || from === to) {
      this.settleFades(nodes, to, attr);
      return;
    }

    nodes.forEach(function (el) {
      var i = parseInt(el.getAttribute(attr), 10);
      if (i === from || i === to) return;
      el.classList.remove('is-active', 'is-exiting');
    });

    if (outgoing) {
      outgoing.classList.remove('is-active');
      outgoing.classList.add('is-exiting');
    }

    incoming.classList.add('is-active');
    incoming.classList.remove('is-exiting');

    this.later(function () {
      if (outgoing) outgoing.classList.remove('is-exiting');
    }, duration);
  };

  FamiliesNew.prototype.goTo = function (next) {
    if (next < 0 || next >= this.total || next === this.index) return;

    var prev = this.index;
    var self = this;

    /* Cancel in-flight timeouts, then snap current target so interrupt is clean. */
    this.clearTimers();
    this.settlePanels(this.bgPanels, prev);
    this.settlePanels(this.scenePanels, prev);
    this.settleFades(this.copies, prev, 'data-family-copy');
    this.settleFades(this.signatures, prev, 'data-family-signature');
    this.settleFades(this.ghosts, prev, 'data-family-ghost');

    this.index = next;

    this.swapPanels(this.bgPanels, prev, next);
    this.swapPanels(this.scenePanels, prev, next);
    this.swapFade(this.copies, prev, next, 'data-family-copy', COPY_MS);
    this.swapFade(this.signatures, prev, next, 'data-family-signature', COPY_MS);
    this.swapFade(this.ghosts, prev, next, 'data-family-ghost', GHOST_MS);
    this.syncTabs(next, false);

    /* Reset expanded state when switching families on mobile */
    this.copyBodies.forEach(function (item) {
      item.expanded = false;
      self.renderCopyItem(item);
    });
  };

  FamiliesNew.prototype.destroy = function () {
    this.clearTimers();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  };

  function initRoot(root) {
    if (!root || instances.has(root)) return;
    instances.set(root, new FamiliesNew(root));
  }

  function destroyRoot(root) {
    if (!root || !instances.has(root)) return;
    instances.get(root).destroy();
    instances.delete(root);
  }

  function initAll(scope) {
    (scope || document)
      .querySelectorAll('[data-section-type="' + SECTION_TYPE + '"]')
      .forEach(initRoot);
  }

  function findRoot(sectionEl) {
    if (!sectionEl) return null;
    if (sectionEl.matches && sectionEl.matches('[data-section-type="' + SECTION_TYPE + '"]')) {
      return sectionEl;
    }
    return sectionEl.querySelector
      ? sectionEl.querySelector('[data-section-type="' + SECTION_TYPE + '"]')
      : null;
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  if (document.readyState !== 'loading') {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = findRoot(event.target);
    if (!root) return;
    destroyRoot(root);
    initRoot(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = findRoot(event.target);
    if (root) destroyRoot(root);
  });
})();
